import api from './apiClient';

/**
 * Envelope used by every CRUD controller added in the V5 backend
 * (`DepartmentController`, the ten lookup controllers, `JobQuoteController`,
 * `JobFloorController`, `AdminEmployeeController`): `{ success, data }`.
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/** Pulls `data` out of the envelope, tolerating handlers that return a bare body. */
export function unwrapEnvelope<T>(body: ApiEnvelope<T> | T): T {
  if (body && typeof body === 'object' && 'data' in (body as ApiEnvelope<T>)) {
    return (body as ApiEnvelope<T>).data;
  }
  return body as T;
}

export interface ResourceClient<T, TPayload = Record<string, unknown>> {
  list: (params?: Record<string, unknown>) => Promise<T[]>;
  get: (id: number | string) => Promise<T>;
  create: (payload: TPayload) => Promise<T>;
  update: (id: number | string, payload: TPayload) => Promise<T>;
  remove: (id: number | string) => Promise<void>;
}

/**
 * Builds a CRUD client for one of the uniform `/api/{resource}` endpoints.
 *
 * @param basePath e.g. `/api/departments`, `/api/job-types`
 */
export function createResourceClient<T, TPayload = Record<string, unknown>>(
  basePath: string
): ResourceClient<T, TPayload> {
  return {
    async list(params) {
      const res = await api.get<ApiEnvelope<T[]>>(basePath, { params });
      return unwrapEnvelope(res.data) ?? [];
    },
    async get(id) {
      const res = await api.get<ApiEnvelope<T>>(`${basePath}/${id}`);
      return unwrapEnvelope(res.data);
    },
    async create(payload) {
      const res = await api.post<ApiEnvelope<T>>(basePath, payload);
      return unwrapEnvelope(res.data);
    },
    async update(id, payload) {
      const res = await api.put<ApiEnvelope<T>>(`${basePath}/${id}`, payload);
      return unwrapEnvelope(res.data);
    },
    async remove(id) {
      await api.delete(`${basePath}/${id}`);
    },
  };
}
